import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Scanner } from '@yudiel/react-qr-scanner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckInPage = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const checkinKey = searchParams.get('key'); // Get key from URL
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [checkInResult, setCheckInResult] = useState(null);
  const [stats, setStats] = useState({ total: 0, checked: 0 });
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [lastScanned, setLastScanned] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null); // 'checkins' | 'confirmados' | 'presenca'
  const [allAttendees, setAllAttendees] = useState([]);
  const [allCheckins, setAllCheckins] = useState([]);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  const loadEvent = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      const foundEvent = res.data.find(e => e.id === eventId);
      if (foundEvent) {
        setEvent(foundEvent);
        setStats({
          total: foundEvent.attendees?.length || 0,
          checked: 0
        });
        loadCheckInStats();
      } else {
        setError('Evento não encontrado');
      }
    } catch (err) {
      setError('Erro ao carregar evento');
    }
    setLoading(false);
  };

  const loadCheckInStats = async () => {
    try {
      // Use public endpoint if we have a key, otherwise try admin token
      if (checkinKey) {
        const res = await axios.get(`${API}/events/${eventId}/checkins-public?key=${checkinKey}`);
        setStats({
          total: res.data.total_attendees,
          checked: res.data.total_checkins
        });
        setRecentCheckins(res.data.checkins.slice(-5).reverse());
        setAllCheckins(res.data.checkins || []);
        setAllAttendees(res.data.attendees || []);
      } else {
        const token = localStorage.getItem('wclub_admin_token');
        if (!token) return;
        
        const res = await axios.get(`${API}/events/${eventId}/checkins`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats({
          total: res.data.total_attendees,
          checked: res.data.total_checkins
        });
        setRecentCheckins(res.data.checkins.slice(-5).reverse());
        setAllCheckins(res.data.checkins || []);
        setAllAttendees(res.data.attendees || []);
      }
    } catch (err) {
      console.log('Could not load stats');
    }
  };

  const handleScan = async (result) => {
    if (!result || !result[0]) return;
    
    const data = result[0].rawValue;
    
    // Prevent duplicate scans
    if (data === lastScanned) return;
    setLastScanned(data);
    
    // Parse QR code data - accept WCLUB: or WCLUB-
    let memberId = null;
    if (data.startsWith('WCLUB:')) {
      memberId = data.replace('WCLUB:', '');
    } else if (data.startsWith('WCLUB-')) {
      memberId = data.replace('WCLUB-', '');
    }
    
    if (!memberId) {
      setCheckInResult({
        success: false,
        message: `QR inválido: ${data.substring(0, 30)}`
      });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      setTimeout(() => {
        setCheckInResult(null);
        setLastScanned(null);
      }, 2500);
      return;
    }
    
    try {
      let res;
      
      // Use public endpoint if we have a key, otherwise use admin endpoint
      if (checkinKey) {
        res = await axios.post(`${API}/events/${eventId}/checkin-public`, {
          member_id: memberId,
          checkin_key: checkinKey
        });
      } else {
        const token = localStorage.getItem('wclub_admin_token');
        res = await axios.post(
          `${API}/events/${eventId}/checkin`,
          { member_id: memberId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setCheckInResult({
        success: true,
        message: res.data.message,
        member: res.data.member
      });
      
      if (navigator.vibrate) navigator.vibrate([200]);
      loadCheckInStats();
      
      setTimeout(() => {
        setCheckInResult(null);
        setLastScanned(null);
      }, 3000);
      
    } catch (err) {
      setCheckInResult({
        success: false,
        message: err.response?.data?.detail || 'Erro ao fazer check-in'
      });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      setTimeout(() => {
        setCheckInResult(null);
        setLastScanned(null);
      }, 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] flex flex-col">
      {/* Header - Liquid Glass */}
      <div className="p-4 backdrop-blur-xl"
        style={{
          background: 'linear-gradient(180deg, rgba(15,15,20,0.95) 0%, rgba(10,10,15,0.9) 100%)',
          borderBottom: '1px solid rgba(139,92,246,0.15)'
        }}>
        <div className="flex items-center gap-3">
          <img src="/w-logo-chrome.png" alt="W" className="w-12 h-12 object-contain" style={{ filter: 'drop-shadow(0 0 10px rgba(139,92,246,0.4))' }} />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-['Jost'] font-semibold text-white/90 truncate" style={{ textShadow: '0 0 15px rgba(255,255,255,0.2)' }}>
              {event?.title}
            </h1>
            <p className="text-xs text-white/40">{event?.location}</p>
          </div>
        </div>
        
        {/* Stats - Clickable Cards */}
        <div className="flex gap-3 mt-4">
          <button 
            onClick={() => setActiveTooltip('checkins')}
            className="flex-1 rounded-2xl p-3 text-center backdrop-blur-xl transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(59,130,246,0.04) 100%)',
              border: '1px solid rgba(139,92,246,0.15)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
            }}
          >
            <p className="text-2xl font-bold text-white" style={{ textShadow: '0 0 20px rgba(139,92,246,0.5)' }}>{stats.checked}</p>
            <p className="text-[10px] text-white/50 uppercase tracking-wider">Check-ins</p>
          </button>
          <button 
            onClick={() => setActiveTooltip('confirmados')}
            className="flex-1 rounded-2xl p-3 text-center backdrop-blur-xl transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.04) 100%)',
              border: '1px solid rgba(59,130,246,0.15)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
            }}
          >
            <p className="text-2xl font-bold text-white" style={{ textShadow: '0 0 20px rgba(59,130,246,0.5)' }}>{stats.total}</p>
            <p className="text-[10px] text-white/50 uppercase tracking-wider">Confirmados</p>
          </button>
          <button 
            onClick={() => setActiveTooltip('presenca')}
            className="flex-1 rounded-2xl p-3 text-center backdrop-blur-xl transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(16,185,129,0.06) 100%)',
              border: '1px solid rgba(34,197,94,0.2)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
            }}
          >
            <p className="text-2xl font-bold text-green-400" style={{ textShadow: '0 0 20px rgba(34,197,94,0.6)' }}>
              {stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0}%
            </p>
            <p className="text-[10px] text-white/50 uppercase tracking-wider">Presença</p>
          </button>
        </div>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {!scanning ? (
          <div className="text-center">
            <button
              onClick={() => setScanning(true)}
              className="w-48 h-48 mx-auto bg-gradient-to-br from-green-500/20 to-green-600/10 border-2 border-green-500/30 rounded-3xl flex flex-col items-center justify-center gap-3 hover:scale-105 transition-transform active:scale-95"
              data-testid="start-scanner-btn"
            >
              <svg className="w-16 h-16 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
              </svg>
              <span className="text-green-400 font-['Jost'] font-semibold">Iniciar Scanner</span>
            </button>
            <p className="text-white/30 text-sm mt-4">Toque para ativar a câmera</p>
          </div>
        ) : (
          <div className="w-full max-w-sm">
            {/* Scanner */}
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-black border-2 border-green-500/30">
              <Scanner
                onScan={handleScan}
                onError={(err) => console.log('Scanner error:', err)}
                constraints={{
                  facingMode: 'environment'
                }}
                formats={['qr_code']}
                components={{
                  audio: false,
                  torch: true
                }}
                styles={{
                  container: {
                    width: '100%',
                    height: '100%'
                  },
                  video: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }
                }}
              />
              
              {/* Corner markers overlay */}
              <div className="absolute inset-0 pointer-events-none p-6">
                <div className="absolute top-6 left-6 w-12 h-12 border-l-4 border-t-4 border-green-400 rounded-tl-xl"></div>
                <div className="absolute top-6 right-6 w-12 h-12 border-r-4 border-t-4 border-green-400 rounded-tr-xl"></div>
                <div className="absolute bottom-6 left-6 w-12 h-12 border-l-4 border-b-4 border-green-400 rounded-bl-xl"></div>
                <div className="absolute bottom-6 right-6 w-12 h-12 border-r-4 border-b-4 border-green-400 rounded-br-xl"></div>
              </div>
            </div>

            <button
              onClick={() => setScanning(false)}
              className="w-full mt-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-sm text-white/50 hover:bg-white/[0.1] transition"
            >
              Parar Scanner
            </button>
            
            <p className="text-white/30 text-xs text-center mt-3">
              Aponte para o QR Code do membro
            </p>
          </div>
        )}
      </div>

      {/* Recent Check-ins */}
      {recentCheckins.length > 0 && !scanning && (
        <div className="p-4 border-t border-white/[0.06]">
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Últimos Check-ins</p>
          <div className="space-y-1">
            {recentCheckins.map((c, i) => (
              <div key={c.id || i} className="flex items-center justify-between py-1.5 px-2 bg-white/[0.02] rounded-lg">
                <span className="text-sm text-white/70">{c.member_name}</span>
                <span className="text-[10px] text-white/30">
                  {new Date(c.checked_in_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Check-in Result Overlay */}
      <AnimatePresence>
        {checkInResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`p-8 rounded-3xl text-center mx-4 ${
                checkInResult.success 
                  ? 'bg-gradient-to-br from-green-500/20 to-green-600/10 border-2 border-green-500/40' 
                  : 'bg-gradient-to-br from-red-500/20 to-red-600/10 border-2 border-red-500/40'
              }`}
            >
              {checkInResult.success ? (
                <>
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{
                      background: 'rgba(34,197,94,0.2)',
                      border: '2px solid rgba(34,197,94,0.4)',
                      boxShadow: '0 0 30px rgba(34,197,94,0.5)'
                    }}
                  >
                    <svg className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                      style={{ color: '#4ade80', filter: 'drop-shadow(0 0 10px rgba(34,197,94,0.8))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <h4 className="text-2xl font-['Jost'] font-bold text-green-400 mb-2" style={{ textShadow: '0 0 15px rgba(34,197,94,0.5)' }}>Check-in OK!</h4>
                  <p className="text-white text-xl font-semibold">{checkInResult.member?.name}</p>
                  {checkInResult.member?.category && (
                    <span className="inline-block mt-3 px-4 py-1.5 bg-white/10 rounded-full text-sm text-white/70 uppercase">
                      {checkInResult.member.category}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{
                      background: 'rgba(239,68,68,0.2)',
                      border: '2px solid rgba(239,68,68,0.4)',
                      boxShadow: '0 0 30px rgba(239,68,68,0.5)'
                    }}
                  >
                    <svg className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                      style={{ color: '#f87171', filter: 'drop-shadow(0 0 10px rgba(239,68,68,0.8))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.div>
                  <h4 className="text-2xl font-['Jost'] font-bold text-red-400 mb-2" style={{ textShadow: '0 0 15px rgba(239,68,68,0.5)' }}>Erro</h4>
                  <p className="text-white/70 text-base">{checkInResult.message}</p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Tooltip Modals - Bottom Sheet */}
      <AnimatePresence>
        {activeTooltip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end justify-center z-50"
            onClick={() => setActiveTooltip(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl overflow-hidden"
              style={{ 
                background: 'linear-gradient(180deg, rgba(15,15,20,0.98) 0%, rgba(5,5,10,0.99) 100%)',
                maxHeight: '70vh'
              }}
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>
              
              {/* Header */}
              <div className="px-5 pb-4 border-b border-white/[0.06]">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold font-['Jost'] text-white/90" style={{ 
                      textShadow: activeTooltip === 'checkins' ? '0 0 15px rgba(139,92,246,0.5)' :
                                  activeTooltip === 'confirmados' ? '0 0 15px rgba(59,130,246,0.5)' :
                                  '0 0 15px rgba(34,197,94,0.5)'
                    }}>
                      {activeTooltip === 'checkins' && `Check-ins (${stats.checked})`}
                      {activeTooltip === 'confirmados' && `Confirmados (${stats.total})`}
                      {activeTooltip === 'presenca' && `Presença (${stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0}%)`}
                    </h3>
                    <p className="text-xs text-white/40 font-['Jost']">{event?.title}</p>
                  </div>
                  <button 
                    onClick={() => setActiveTooltip(null)}
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                  >
                    <svg className="w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-5 py-4 max-h-[50vh] overflow-y-auto space-y-2">
                {activeTooltip === 'checkins' && (
                  <>
                    {allCheckins.length === 0 ? (
                      <p className="text-white/30 text-sm text-center py-6">Nenhum check-in ainda</p>
                    ) : (
                      allCheckins.map((c, i) => (
                        <div key={c.id || i} className="flex items-center justify-between p-3 rounded-2xl backdrop-blur-xl"
                          style={{
                            background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(16,185,129,0.04) 100%)',
                            border: '1px solid rgba(34,197,94,0.15)'
                          }}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ background: 'rgba(34,197,94,0.15)', boxShadow: '0 0 10px rgba(34,197,94,0.3)' }}>
                              <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="text-sm text-white/80 font-medium">{c.member_name}</span>
                          </div>
                          <span className="text-[11px] text-white/40">
                            {new Date(c.checked_in_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))
                    )}
                  </>
                )}
                
                {activeTooltip === 'confirmados' && (
                  <>
                    {allAttendees.length === 0 ? (
                      <p className="text-white/30 text-sm text-center py-6">Nenhum confirmado</p>
                    ) : (
                      allAttendees.map((a, i) => {
                        const hasCheckedIn = allCheckins.some(c => c.member_id === a.id);
                        return (
                          <div key={a.id || i} className="flex items-center justify-between p-3 rounded-2xl backdrop-blur-xl"
                            style={{
                              background: hasCheckedIn 
                                ? 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(16,185,129,0.04) 100%)'
                                : 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.04) 100%)',
                              border: hasCheckedIn 
                                ? '1px solid rgba(34,197,94,0.15)'
                                : '1px solid rgba(59,130,246,0.15)'
                            }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center"
                                style={hasCheckedIn ? { 
                                  background: 'rgba(34,197,94,0.15)', 
                                  boxShadow: '0 0 10px rgba(34,197,94,0.3)' 
                                } : {
                                  background: 'rgba(59,130,246,0.15)'
                                }}>
                                {hasCheckedIn ? (
                                  <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <span className="text-[10px] text-blue-400 font-semibold">{i + 1}</span>
                                )}
                              </div>
                              <span className="text-sm text-white/80 font-medium">{a.name}</span>
                            </div>
                            <span className={`text-[10px] px-2 py-1 rounded-full ${
                              hasCheckedIn 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {hasCheckedIn ? 'Presente' : 'Aguardando'}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </>
                )}
                
                {activeTooltip === 'presenca' && (
                  <div className="py-4 space-y-4">
                    {/* Progress Ring */}
                    <div className="flex justify-center">
                      <div className="relative w-32 h-32">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="64" cy="64" r="56" stroke="rgba(255,255,255,0.1)" strokeWidth="8" fill="none" />
                          <circle 
                            cx="64" cy="64" r="56" 
                            stroke="url(#presencaGrad)" 
                            strokeWidth="8" 
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${(stats.checked / Math.max(stats.total, 1)) * 352} 352`}
                            style={{ filter: 'drop-shadow(0 0 8px rgba(34,197,94,0.5))' }}
                          />
                          <defs>
                            <linearGradient id="presencaGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#4ade80" />
                              <stop offset="100%" stopColor="#22c55e" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center flex-col">
                          <span className="text-3xl font-bold text-green-400" style={{ textShadow: '0 0 20px rgba(34,197,94,0.5)' }}>
                            {stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0}%
                          </span>
                          <span className="text-[10px] text-white/40">presença</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stats Summary */}
                    <div className="flex gap-3">
                      <div className="flex-1 p-3 rounded-2xl text-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(16,185,129,0.05) 100%)',
                          border: '1px solid rgba(34,197,94,0.2)'
                        }}>
                        <p className="text-xl font-bold text-green-400">{stats.checked}</p>
                        <p className="text-[10px] text-white/40">Presentes</p>
                      </div>
                      <div className="flex-1 p-3 rounded-2xl text-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(251,191,36,0.1) 0%, rgba(245,158,11,0.05) 100%)',
                          border: '1px solid rgba(251,191,36,0.2)'
                        }}>
                        <p className="text-xl font-bold text-amber-400">{stats.total - stats.checked}</p>
                        <p className="text-[10px] text-white/40">Aguardando</p>
                      </div>
                      <div className="flex-1 p-3 rounded-2xl text-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.05) 100%)',
                          border: '1px solid rgba(59,130,246,0.2)'
                        }}>
                        <p className="text-xl font-bold text-blue-400">{stats.total}</p>
                        <p className="text-[10px] text-white/40">Total</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-5 py-4 border-t border-white/[0.06]">
                <button 
                  onClick={() => setActiveTooltip(null)}
                  className="w-full py-3.5 rounded-2xl text-sm font-medium backdrop-blur-xl transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.6)'
                  }}
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CheckInPage;
